import { useEffect, useState } from 'react';
import { SAVED_REPLY_TOKENS, type SavedReply } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { useCreateSavedReply, useUpdateSavedReply } from '../../hooks/useSavedReplies';

export interface SavedReplyModalProps {
  open: boolean;
  onClose: () => void;
  /** The reply to edit; omitted to write a new one. */
  reply?: SavedReply | null;
  /** What a new reply starts from — "Save as a reply" hands in the draft. */
  seed?: { subject?: string | null; body?: string };
  onSaved?: (reply: SavedReply) => void;
}

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Write or edit a saved reply: a name, an optional subject, the wording with
 * its `{{merge tags}}`, and whether the office shares it.
 *
 * The tags are offered as chips that append themselves, because a tag typed
 * from memory is the one spelled `{{client}}` that never fills.
 */
export function SavedReplyModal({ open, onClose, reply, seed, onSaved }: SavedReplyModalProps) {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateSavedReply();
  const update = useUpdateSavedReply();
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (!open) return;
    setName(reply?.name ?? '');
    setSubject(reply?.subject ?? seed?.subject ?? '');
    setBody(reply?.body ?? seed?.body ?? '');
    setIsShared(reply?.isShared ?? false);
    setError(null);
    // Seeded once per opening: the draft behind it keeps changing.
  }, [open, reply]);

  function save() {
    if (!name.trim()) {
      setError('Give it a name you will recognise it by.');
      return;
    }
    if (!body.trim()) {
      setError('Write the reply itself.');
      return;
    }
    setError(null);
    const input = { name: name.trim(), subject: subject.trim() || null, body, isShared };
    const done = {
      onSuccess: (saved: SavedReply) => {
        onSaved?.(saved);
        onClose();
      },
      onError: (err: Error) => setError(err.message || 'The reply could not be saved.'),
    };
    if (reply) update.mutate({ id: reply.id, input }, done);
    else create.mutate(input, done);
  }

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={reply ? 'Edit saved reply' : 'New saved reply'}
      size="md"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            data-testid="saved-reply-save"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="text-sm text-red-600" role="alert" data-testid="saved-reply-error">
            {error}
          </p>
        )}
        <div>
          <label
            htmlFor="saved-reply-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Name
          </label>
          <input
            id="saved-reply-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Deposit reminder"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label
            htmlFor="saved-reply-subject"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Subject <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            id="saved-reply-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Used when the message has a subject that is still empty"
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label
            htmlFor="saved-reply-body"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Reply
          </label>
          <textarea
            id="saved-reply-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={7}
            className={INPUT_CLASS}
          />
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="saved-reply-tokens">
            {SAVED_REPLY_TOKENS.map((t) => (
              <button
                key={t.token}
                type="button"
                title={`e.g. ${t.example}`}
                onClick={() => setBody((current) => `${current}{{${t.token}}}`)}
                className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700 hover:bg-primary-50 hover:text-primary-700"
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Tags are filled from the event, vendor or record the message is about. One that has no
            value is left as [Label] for you to fill in.
          </p>
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            className="mt-0.5"
            data-testid="saved-reply-shared"
          />
          <span>
            Share with the office
            <span className="block text-xs text-gray-500">
              Everybody can use it; only you and administrators can change it.
            </span>
          </span>
        </label>
      </div>
    </EntityModal>
  );
}
